<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { TemplateField } from '@/api';
import { selectTemplateFieldInDocument } from '@/office/word/templateWord';
import { isEditableDetectedField } from '@/office/word/templateFieldEditing';
import {
  FIELD_TYPE_LABEL,
  FIELD_TYPE_TAG,
  flattenFields,
  type FlattenedField,
} from '@/utils/contractGeneration/templateSchemaFields';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCard from '@/components/design/base/GaiaCard.vue';
import GaiaCountPill from '@/components/design/base/GaiaCountPill.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import {
  gaiaFonts,
  gaiaFontSize,
  gaiaFontWeight,
  gaiaNeutral,
} from '@/components/design/themes/tokens';
import { useGaiaToast } from '@/composables/useGaiaToast';

/**
 * Collapsible list of a template schema's fields — the task-pane form of the
 * web variant-detail page's field list, drawn the same way: mono names, the
 * type chip, the Required chip, container rows that expand and collapse.
 * Clicking a row searches the open Word document for the field's Jinja tag
 * and selects it. Used for both the stored schema (My templates tab) and live
 * validation results (Editor tab). With `editable`, scalar leaf rows get an
 * edit button that emits the field for the host to open the config drawer on
 * — only meaningful where the fields reflect the live document, so the Editor
 * tab opts in and My templates stays view-only.
 */

const props = withDefaults(
  defineProps<{
    fields: { [key: string]: TemplateField } | null | undefined;
    title?: string;
    defaultExpanded?: boolean;
    editable?: boolean;
  }>(),
  {
    title: 'Fields',
    defaultExpanded: false,
    editable: false,
  }
);

const emit = defineEmits<{
  (e: 'edit', field: FlattenedField): void;
}>();

const flatFields = computed<FlattenedField[]>(() => flattenFields(props.fields ?? undefined));

/** The whole card folds to its header, so a long list can get out of the way. */
const open = ref<boolean>(props.defaultExpanded);

// ── Per-field expand/collapse (mirrors the web variant-detail view) ─────

const expandedPaths = ref<Set<string>>(new Set());

watch(
  flatFields,
  () => {
    expandedPaths.value = new Set();
  },
  { immediate: true }
);

function isExpanded(path: string): boolean {
  return expandedPaths.value.has(path);
}

function toggleExpanded(path: string): void {
  const next = new Set(expandedPaths.value);
  if (next.has(path)) next.delete(path);
  else next.add(path);
  expandedPaths.value = next;
}

const hasParents = computed<boolean>(() => flatFields.value.some(field => field.hasChildren));

const allExpanded = computed<boolean>(() => {
  const parents = flatFields.value.filter(field => field.hasChildren);
  if (parents.length === 0) return true;
  return parents.every(field => expandedPaths.value.has(field.path));
});

function toggleExpandAll(): void {
  expandedPaths.value = allExpanded.value
    ? new Set()
    : new Set(flatFields.value.filter(field => field.hasChildren).map(field => field.path));
}

// ── Search ───────────────────────────────────────────────────────────────

const searchQuery = ref<string>('');

const searching = computed<boolean>(() => searchQuery.value.trim().length > 0);

/** Worth a search bar only once the list is long enough to lose a field in. */
const showSearch = computed<boolean>(() => flatFields.value.length > 5);

/**
 * Matching on the path as well as the display name means a container's name
 * also hits every row nested under it (child paths embed each ancestor
 * segment), so searching a role or group shows its whole subtree.
 */
function rowMatches(field: FlattenedField, query: string): boolean {
  return field.displayKey.toLowerCase().includes(query) || field.path.toLowerCase().includes(query);
}

/**
 * Matching rows plus their ancestors — rows only show their leaf name, so a
 * hit like `signed_date` needs its `contract` parent above it to make sense.
 */
function searchFields(fields: FlattenedField[], query: string): FlattenedField[] {
  const out: FlattenedField[] = [];
  const includedPaths = new Set<string>();
  const ancestors: FlattenedField[] = [];
  for (const field of fields) {
    while ((ancestors.at(-1)?.depth ?? -Infinity) >= field.depth) {
      ancestors.pop();
    }
    if (rowMatches(field, query)) {
      for (const ancestor of ancestors) {
        if (!includedPaths.has(ancestor.path)) {
          includedPaths.add(ancestor.path);
          out.push(ancestor);
        }
      }
      includedPaths.add(field.path);
      out.push(field);
    }
    if (field.hasChildren) ancestors.push(field);
  }
  return out;
}

/**
 * Rows to render. While searching, the collapse state is bypassed (results
 * show force-expanded). Otherwise, skips rows whose ancestor (at any depth)
 * is currently collapsed. A single `collapsedAtDepth` tracker suffices
 * because the flat list is in pre-order — once inside a collapsed subtree,
 * every following deeper row belongs to it, and the first row at a
 * shallower-or-equal depth means we've left it.
 */
const visibleFields = computed<FlattenedField[]>(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (query) return searchFields(flatFields.value, query);
  const out: FlattenedField[] = [];
  let collapsedAtDepth: number | null = null;
  for (const field of flatFields.value) {
    if (collapsedAtDepth !== null && field.depth > collapsedAtDepth) continue;
    collapsedAtDepth = null;
    out.push(field);
    if (field.hasChildren && !expandedPaths.value.has(field.path)) {
      collapsedAtDepth = field.depth;
    }
  }
  return out;
});

// ── Click-to-locate ──────────────────────────────────────────────────────
//
// A field used in several places cycles: each click on the same row jumps to
// the next occurrence and wraps around. Switching to a different field (or
// re-rendering the list) restarts at the first occurrence.

const message = useGaiaToast();
const locatingPath = ref<string | null>(null);
// Cycle state for the field being stepped through. `current` is the 1-based
// occurrence last jumped to; shown inline as `current/total` next to the name
// whenever a field has more than one occurrence.
const cyclePath = ref<string | null>(null);
const cycleCurrent = ref<number>(0);
const cycleTotal = ref<number>(0);

function resetCycle(): void {
  cyclePath.value = null;
  cycleCurrent.value = 0;
  cycleTotal.value = 0;
}

watch(flatFields, resetCycle);

async function handleFieldClick(field: FlattenedField): Promise<void> {
  if (locatingPath.value) return;
  locatingPath.value = field.path;
  // Continue the cycle for the same field; restart for a different one.
  const requestedIndex = cyclePath.value === field.path ? cycleCurrent.value : 0;
  try {
    const result = await selectTemplateFieldInDocument(field.path, requestedIndex);
    if (!result.found) {
      resetCycle();
      message.warning(`Couldn't find "${field.displayKey}" in the document.`);
      return;
    }
    cyclePath.value = field.path;
    cycleCurrent.value = result.index + 1;
    cycleTotal.value = result.total;
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to locate the field.');
  } finally {
    locatingPath.value = null;
  }
}

// --- chrome (the web field list's own recipe, on the shared tokens) ---------
const rowBorder = gaiaNeutral.borderSoft;
const rowHover = gaiaNeutral.surfaceHover;
const chevronColor = gaiaNeutral.textSubtle;
const monoFont = gaiaFonts.mono;
const nameSize = gaiaFontSize.meta;
const nameWeight = String(gaiaFontWeight.semibold);
const inkColor = gaiaNeutral.ink;
const faintColor = gaiaNeutral.textFaint;
</script>

<template>
  <gaia-card size="small" content-style="padding: 0">
    <!-- The card head doubles as the fold toggle; the count says how much is
         folded away. -->
    <button type="button" class="card-head" :aria-expanded="open" @click="open = !open">
      <svg
        width="10"
        height="10"
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
        stroke-width="1.6"
        stroke-linecap="round"
        class="chevron"
        :class="{ open }"
      >
        <path d="M4.5 2.5 8 6l-3.5 3.5" />
      </svg>
      <gaia-text size="dense" weight="semibold" tone="ink">{{ title }}</gaia-text>
      <gaia-count-pill>{{ flatFields.length }}</gaia-count-pill>
    </button>

    <div v-if="open" class="card-body">
      <gaia-empty-state v-if="flatFields.length === 0" dashed title="No fields detected">
        This template has no placeholders to fill in yet.
      </gaia-empty-state>

      <template v-else>
        <n-flex vertical :size="8" :wrap="false" :style="{ padding: '10px 12px 8px' }">
          <gaia-search-input
            v-if="showSearch"
            v-model:value="searchQuery"
            variant="panel"
            placeholder="Search fields"
          />
          <n-flex align="center" justify="space-between" :size="8" :wrap="false">
            <gaia-text size="micro" tone="subtle" :style="{ minWidth: 0 }">
              Click a field to find it in the document.{{
                editable ? ' The pencil edits it everywhere it appears.' : ''
              }}
            </gaia-text>
            <gaia-button
              v-if="hasParents && !searching"
              text
              size="tiny"
              :style="{ flex: 'none' }"
              @click="toggleExpandAll"
            >
              {{ allExpanded ? 'Collapse all' : 'Expand all' }}
            </gaia-button>
          </n-flex>
        </n-flex>

        <gaia-empty-state v-if="searching && visibleFields.length === 0" title="No fields match">
          Try a shorter term.
        </gaia-empty-state>

        <ul v-else class="field-list">
          <li
            v-for="field in visibleFields"
            :key="field.path"
            class="field-row"
            :class="{ dimmed: locatingPath && locatingPath !== field.path }"
            :style="{ paddingLeft: `${12 + field.depth * 14}px` }"
            :title="`Find ${field.displayKey} in the document`"
            @click="handleFieldClick(field)"
          >
            <button
              v-if="field.hasChildren && !searching"
              type="button"
              class="chevron-button"
              :aria-label="(isExpanded(field.path) ? 'Collapse' : 'Expand') + ' ' + field.key"
              @click.stop="toggleExpanded(field.path)"
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                class="chevron"
                :class="{ open: isExpanded(field.path) }"
              >
                <path d="M4.5 2.5 8 6l-3.5 3.5" />
              </svg>
            </button>
            <span v-else class="chevron placeholder" />

            <n-flex align="baseline" :size="6" :wrap="false" :style="{ minWidth: 0, flex: 1 }">
              <span class="field-name">{{ field.displayKey }}</span>
              <gaia-text
                v-if="cyclePath === field.path && cycleTotal > 1"
                size="micro"
                tone="subtle"
                :style="{ flex: 'none', fontVariantNumeric: 'tabular-nums' }"
              >
                {{ cycleCurrent }}/{{ cycleTotal }}
              </gaia-text>
            </n-flex>

            <n-flex align="center" :size="4" :wrap="false" :style="{ flex: 'none' }">
              <gaia-tooltip v-if="editable && isEditableDetectedField(field)">
                <template #trigger>
                  <gaia-icon-button
                    :dimension="24"
                    quaternary
                    :aria-label="`Edit ${field.displayKey}`"
                    @click.stop="emit('edit', field)"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M3 13.2 12.2 4l-1.7-1.7L1.3 11.5 1 14z" />
                    </svg>
                  </gaia-icon-button>
                </template>
                Edit field
              </gaia-tooltip>
              <gaia-tag size="small" :type="FIELD_TYPE_TAG[field.type] ?? 'default'" round>
                {{ FIELD_TYPE_LABEL[field.type] ?? field.type }}
                <template v-if="field.type === 'list' && field.itemType">
                  &lt;{{ FIELD_TYPE_LABEL[field.itemType] ?? field.itemType }}&gt;
                </template>
              </gaia-tag>
              <gaia-tag v-if="field.required" size="small" type="error" round>Required</gaia-tag>
            </n-flex>
          </li>
        </ul>
      </template>
    </div>
  </gaia-card>
</template>

<style scoped>
/* The fold toggle is the card's head: a full-width button so the whole row
   takes the click, with the mono name / chevron recipe the web field list
   draws. n-collapse could fold the body but not carry this head. */
.card-head {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 11px 12px;
  border: none;
  background: none;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.card-head:hover {
  background: v-bind(rowHover);
}

.card-body {
  border-top: 1px solid v-bind(rowBorder);
}

.chevron {
  flex: none;
  width: 10px;
  color: v-bind(chevronColor);
  transition: transform 0.15s;
}

.chevron.open {
  transform: rotate(90deg);
}

.chevron-button {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
}

.chevron.placeholder {
  width: 16px;
}

.field-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.field-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid v-bind(rowBorder);
  cursor: pointer;
  transition: opacity 0.15s;
}

.field-row:hover {
  background: v-bind(rowHover);
}

.field-row.dimmed {
  opacity: 0.5;
}

.field-name {
  font-family: v-bind(monoFont);
  font-size: v-bind(nameSize);
  font-weight: v-bind(nameWeight);
  color: v-bind(inkColor);
  overflow-wrap: anywhere;
}

.field-name:empty {
  color: v-bind(faintColor);
}
</style>
